import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { SiLinkedin, SiGithub, SiX } from "react-icons/si";
import { useNavigate } from "react-router-dom"; // <-- import useNavigate

const ExperiencePage = ({ experience }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (index) => setOpenIndex(openIndex === index ? null : index);
  const navigate = useNavigate(); // <-- initialize navigate

  return (
    <section id="experience" className="px-4 sm:px-6 md:px-10 py-20 relative">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12 text-start">
        Experience
      </h2>

      <div className="max-w-4xl mx-auto space-y-6">
        {experience.map((exp, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
            viewport={{ once: true }}
            className="bg-black-100 dark:bg-black-800 rounded-2xl border border-gray-800 dark:border-gray-800 p-5 sm:p-6"
          >
            {/* Header Row */}
            <button
              onClick={() => toggle(i)}
              className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-left"
            >
              <div className="flex items-start gap-4 w-full">
                {/* Logo */}
                <img
                  src={exp.logo}
                  alt="logo"
                  loading="lazy"
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-contain bg-gray-200 dark:bg-gray-700 p-2 border border-gray-300 dark:border-gray-600"
                />

                <div className="min-w-0 flex-1">
                  {/* Company + Social Icons */}
                  <div className="flex items-center flex-wrap gap-2">
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {exp.company}
                    </h3>

                    {/* Desktop Icons + Arrow */}
                    <div className="hidden sm:flex items-center gap-2 ml-2">
                      {exp.linkedin && (
                        <a
                          href={exp.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative group"
                        >
                          <SiLinkedin
                            className="text-black dark:text-gray-300 hover:scale-105 transition"
                            size={17}
                          />

                          {/* Tooltip */}
                          <span className="tooltip">LinkedIn</span>
                        </a>
                      )}

                      {exp.github && (
                        <a
                          href={exp.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative group"
                        >
                          <SiGithub
                            className="text-black dark:text-gray-300 hover:scale-105 transition"
                            size={17}
                          />

                          <span className="tooltip">GitHub</span>
                        </a>
                      )}

                      {exp.twitter && (
                        <a
                          href={exp.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative group"
                        >
                          <SiX
                            className="text-black dark:text-gray-300 hover:scale-105 transition"
                            size={17}
                          />

                          <span className="tooltip">Twitter / X</span>
                        </a>
                      )}

                      {/* Arrow (close on desktop/tablet) */}
                      <div className="relative group cursor-pointer">
                        {openIndex === i ? (
                          <ChevronUp
                            className="text-gray-700 dark:text-gray-300"
                            size={22}
                          />
                        ) : (
                          <ChevronDown
                            className="text-gray-700 dark:text-gray-300"
                            size={22}
                          />
                        )}

                        <span className="tooltip">Expand</span>
                      </div>
                    </div>
                  </div>

                  {/* Role */}
                  <p className="text-sm md:text-base mt-1 text-gray-500 dark:text-gray-500">
                    {exp.role}
                  </p>

                  {/* Mobile layout: Date + Arrow on far-right */}
                  <div className="flex sm:hidden justify-between items-center mt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <p>{exp.date}</p>
                      {exp.location && <p>{exp.location}</p>}
                    </div>

                    {/* Arrow tooltip mobile */}
                    <div className="relative group cursor-pointer">
                      {openIndex === i ? (
                        <ChevronUp
                          size={22}
                          className="text-gray-700 dark:text-gray-300 ml-auto"
                        />
                      ) : (
                        <ChevronDown
                          size={22}
                          className="text-gray-700 dark:text-gray-300 ml-auto"
                        />
                      )}

                      <span className="tooltip">Tap to expand</span>
                    </div>
                  </div>
                </div>

                {/* Desktop date / location */}
                <div className="hidden sm:flex flex-col items-end text-right">
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-500">
                    {exp.date}
                  </p>
                  {exp.location && (
                    <p className="text-xs md:text-sm text-gray-500 dark:text-gray-500">
                      {exp.location}
                    </p>
                  )}
                </div>
              </div>
            </button>

            {/* Expandable Section */}
            {/* Expandable Section */}
            <AnimatePresence>
              {openIndex === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="overflow-hidden mt-4"
                >
                  {/* Technologies & Tools */}
                  {/* Technologies & Tools */}
                  {exp.technologies && exp.technologies.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Technologies & Tools
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {exp.technologies.map((tech, idx) => (
                          <a
                            key={idx}
                            href={tech.link || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full text-sm font-medium text-gray-900 dark:text-gray-100 shadow-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                          >
                            {tech.icon && (
                              <span className="flex items-center justify-center text-lg text-gray-700 dark:text-gray-200">
                                {tech.icon}
                              </span>
                            )}
                            <span>{tech.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Achievements / Contributions */}
                  <ul className="mt-2 space-y-2 text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed pl-5 list-disc">
                    {exp.points.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}

        {/* Show All Work Experience Button */}
        <div className="flex justify-center mt-10">
          <button
            onClick={() => navigate("/work-experience")}
            className="px-6 py-2 bg-black-100 dark:bg-black-800 text-black dark:text-white font-semibold rounded-lg border border-gray-300 dark:border-gray-600 transition hover:opacity-90"
          >
            Show All Work Experience
          </button>
        </div>
      </div>
    </section>
  );
};

export default ExperiencePage;
